import {
  MVP_FACTIONS,
  MATCH_MODE_IDS,
  MAX_PLAYERS_PER_FACTION,
  RICHNESS,
  RICHNESS_IDS,
  type FactionId,
  type MatchModeId,
  type RichnessId,
} from '@sd/content';
import { LOCALES, t, type Locale } from '@sd/i18n';
import {
  BOT_LEVELS,
  PERSONALITY_IDS,
  type BotLevel,
  type MatchSetup,
  type PersonalityId,
  type Seat,
} from '@sd/bots';
import { escapeHtml, seatColor } from './format';

/**
 * The screen before a match (#53): map seed, number of players, the
 * player's faction, each bot's faction and character, how the match ends.
 */
export interface LobbyChoice {
  seed: string;
  players: number;
  mode: MatchModeId;
  you: FactionId;
  /** Your team (#137); none = everyone for themselves. */
  team?: number | undefined;
  opponents: { faction: FactionId; character: PersonalityId; team?: number | undefined }[];
  /** How rich the system is (#98). */
  richness: RichnessId;
  /** Finds picked from three cards (#105, a test). */
  findChoice: boolean;
  /** How quick the bots are (#148). */
  botLevel: BotLevel;
}

const FACTIONS = MVP_FACTIONS.map((f) => f.id);
const CHARACTERS: readonly PersonalityId[] = ['raider', 'trader', 'scientist'];
/** Team numbers a seat can take (#137). */
const TEAMS = [1, 2, 3, 4] as const;

export function defaultChoice(seed: string, players: number): LobbyChoice {
  return {
    seed,
    players,
    mode: 'crisisFinale',
    richness: 'balanced',
    findChoice: true,
    botLevel: 'normal',
    you: 'earthborn',
    opponents: [0, 1, 2].map((i) => ({
      faction: FACTIONS[(i + 1) % FACTIONS.length]!,
      character: CHARACTERS[i]!,
    })),
  };
}

export function toSetup(choice: LobbyChoice): MatchSetup {
  const team = (n: number | undefined) => (n === undefined ? {} : { team: n });
  const seats: Seat[] = [
    { id: 'p1', faction: choice.you, control: 'human', ...team(choice.team) },
    ...choice.opponents.slice(0, choice.players - 1).map((o, i) => ({
      id: `p${i + 2}`,
      faction: o.faction,
      control: o.character,
      ...team(o.team),
    })),
  ];
  return {
    seed: choice.seed,
    mode: choice.mode,
    richness: choice.richness,
    ...(choice.findChoice ? { findChoice: true } : {}),
    botLevel: choice.botLevel,
    seats,
  };
}

/** Factions over the cap, if any: the match generator would refuse them. */
export function crowdedFaction(choice: LobbyChoice): FactionId | null {
  const counts = new Map<FactionId, number>();
  for (const seat of toSetup(choice).seats)
    counts.set(seat.faction, (counts.get(seat.faction) ?? 0) + 1);
  for (const [faction, count] of counts) if (count > MAX_PLAYERS_PER_FACTION) return faction;
  return null;
}

/** What a richness puts around every capital, from its numbers in content. */
export function richnessHint(locale: Locale, r: RichnessId): string {
  const { clouds, gasGiants, planets, asteroids } = RICHNESS[r].perCapital;
  return t(locale, 'richness.hint', {
    c1: clouds[1],
    c2: clouds[2],
    c3: clouds[3],
    giants: gasGiants,
    planets,
    asteroids,
  });
}

export interface LobbyHandlers {
  onChange(choice: LobbyChoice): void;
  onLocale(locale: Locale): void;
  onStart(setup: MatchSetup): void;
  randomSeed(): string;
  /** The seed is a dev tool (#98): players get a fresh system every match. */
  showSeed?: boolean;
  /** Back to the main menu (#139). */
  back?: () => void;
}

export function renderLobby(
  root: HTMLElement,
  locale: Locale,
  choice: LobbyChoice,
  on: LobbyHandlers,
) {
  const crowded = crowdedFaction(choice);
  const factionSelect = (name: string, value: FactionId) => `
    <select class="sd-select" name="${name}" aria-label="${t(locale, 'ui.lobby.faction')}">
      ${FACTIONS.map(
        (f) =>
          `<option value="${f}" ${f === value ? 'selected' : ''}>${t(locale, `faction.${f}.name`)}</option>`,
      ).join('')}
    </select>`;
  const teamSelect = (name: string, value: number | undefined) => `
    <select class="sd-select sd-select--team" name="${name}" aria-label="${t(locale, 'ui.lobby.team')}">
      <option value="" ${value === undefined ? 'selected' : ''}>${t(locale, 'ui.lobby.noTeam')}</option>
      ${TEAMS.map(
        (n) =>
          `<option value="${n}" ${n === value ? 'selected' : ''}>${t(locale, 'ui.lobby.teamN', { n })}</option>`,
      ).join('')}
    </select>`;
  const seats = choice.opponents
    .slice(0, choice.players - 1)
    .map(
      (o, i) => `
      <li class="sd-seat" style="--seat:${seatColor(`p${i + 2}`)}">
        <span class="sd-emblem sd-emblem--sm"></span>
        <span class="sd-seat__name">${t(locale, o.team !== undefined && o.team === choice.team ? 'ui.lobby.ally' : 'ui.lobby.opponent', { n: i + 1 })}</span>
        ${factionSelect(`faction-${i}`, o.faction)}
        ${teamSelect(`team-${i}`, o.team)}
        <select class="sd-select" name="character-${i}" aria-label="${t(locale, 'ui.lobby.character')}">
          ${PERSONALITY_IDS.map(
            (p) =>
              `<option value="${p}" ${p === o.character ? 'selected' : ''}>${t(locale, `personality.${p}.name`)}</option>`,
          ).join('')}
        </select>
        <small class="sd-seat__hint">${t(locale, `personality.${o.character}.short`)}</small>
      </li>`,
    )
    .join('');

  root.innerHTML = `
    <section class="sd-panel sd-panel--strong sd-lobby" aria-labelledby="lobby-title">
      <header class="sd-lobby__head">
        <h1>${t(locale, 'game.title')}</h1>
        <p class="sd-muted">${t(locale, 'game.tagline')}</p>
      </header>
      <h2 id="lobby-title">${t(locale, 'ui.lobby.title')}</h2>
      ${
        on.showSeed
          ? `<div class="sd-field">
        <label class="sd-label" for="lobby-seed">${t(locale, 'ui.lobby.seed')}</label>
        <div class="sd-buttons">
          <input class="sd-input" id="lobby-seed" name="seed" value="${escapeHtml(choice.seed)}" maxlength="24" spellcheck="false" />
          <button class="sd-btn" type="button" data-action="random-seed">${t(locale, 'ui.lobby.randomSeed')}</button>
        </div>
      </div>`
          : ''
      }
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.players')}</span>
        <div class="sd-segmented" role="group">
          ${[2, 3, 4]
            .map(
              (n) =>
                `<button class="sd-btn" type="button" data-players="${n}" aria-pressed="${n === choice.players}">${n}</button>`,
            )
            .join('')}
        </div>
      </div>
      <ol class="sd-seats">
        <li class="sd-seat" style="--seat:${seatColor('p1')}">
          <span class="sd-emblem sd-emblem--sm"></span>
          <span class="sd-seat__name">${t(locale, 'ui.lobby.you')}</span>
          ${factionSelect('you', choice.you)}
          ${teamSelect('myteam', choice.team)}
        </li>
        ${seats}
      </ol>
      ${crowded ? `<p class="sd-error" role="alert">${t(locale, 'ui.lobby.tooMany', { count: MAX_PLAYERS_PER_FACTION })}</p>` : ''}
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.richness')}</span>
        <div class="sd-segmented" role="group">
          ${RICHNESS_IDS.map(
            (r) =>
              `<button class="sd-btn" type="button" data-richness="${r}" aria-pressed="${r === choice.richness}" title="${richnessHint(locale, r)}">${t(locale, `richness.${r}.name`)}</button>`,
          ).join('')}
        </div>
      </div>
      <div class="sd-field" title="${t(locale, 'ui.lobby.findChoiceHint')}">
        <span class="sd-label">${t(locale, 'ui.lobby.findChoice')}</span>
        <div class="sd-segmented" role="group">
          ${[true, false]
            .map(
              (on) =>
                `<button class="sd-btn" type="button" data-find-choice="${on}" aria-pressed="${on === choice.findChoice}">${t(locale, on ? 'ui.lobby.findChoice.on' : 'ui.lobby.findChoice.off')}</button>`,
            )
            .join('')}
        </div>
      </div>
      <div class="sd-field" title="${t(locale, 'ui.lobby.botLevelHint')}">
        <span class="sd-label">${t(locale, 'ui.lobby.botLevel')}</span>
        <div class="sd-segmented" role="group">
          ${BOT_LEVELS.map(
            (l) =>
              `<button class="sd-btn" type="button" data-bot-level="${l}" aria-pressed="${l === choice.botLevel}" title="${t(locale, `botLevel.${l}.hint`)}">${t(locale, `botLevel.${l}.name`)}</button>`,
          ).join('')}
        </div>
      </div>
      <div class="sd-field">
        <span class="sd-label">${t(locale, 'ui.lobby.mode')}</span>
        <div class="sd-segmented" role="group">
          ${MATCH_MODE_IDS.map(
            (m) =>
              `<button class="sd-btn" type="button" data-mode="${m}" aria-pressed="${m === choice.mode}">${t(locale, `mode.${m}.name`)}</button>`,
          ).join('')}
        </div>
      </div>
      <footer class="sd-lobby__foot">
        ${on.back ? `<button class="sd-btn" type="button" data-action="back">${t(locale, 'net.back')}</button>` : ''}
        <div class="sd-segmented" role="group" aria-label="${t(locale, 'ui.language')}">
          ${LOCALES.map(
            (l) =>
              `<button class="sd-btn" type="button" data-locale="${l}" aria-pressed="${l === locale}">${l.toUpperCase()}</button>`,
          ).join('')}
        </div>
        <button class="sd-btn sd-btn--primary sd-btn--large" type="button" data-action="start" ${crowded ? 'disabled' : ''}>
          ${t(locale, 'ui.lobby.start')}
        </button>
      </footer>
    </section>`;

  const update = (patch: Partial<LobbyChoice>) => on.onChange({ ...choice, ...patch });

  root.onclick = (event) => {
    const button = (event.target as HTMLElement).closest('button');
    if (!button || button.disabled) return;
    const { action, players, mode, richness, findChoice, botLevel, locale: next } = button.dataset;
    if (action === 'back') on.back?.();
    else if (action === 'random-seed') update({ seed: on.randomSeed() });
    else if (action === 'start') on.onStart(toSetup(choice));
    else if (players) update({ players: Number(players) });
    else if (mode) update({ mode: mode as MatchModeId });
    else if (richness) update({ richness: richness as RichnessId });
    else if (findChoice) update({ findChoice: findChoice === 'true' });
    else if (botLevel) update({ botLevel: botLevel as BotLevel });
    else if (next) on.onLocale(next as Locale);
  };
  root.onchange = (event) => {
    const field = event.target as HTMLInputElement | HTMLSelectElement;
    const [kind, index] = field.name.split('-');
    if (kind === 'seed') update({ seed: field.value.trim() || choice.seed });
    else if (kind === 'you') update({ you: field.value as FactionId });
    else if (kind === 'myteam') update({ team: field.value ? Number(field.value) : undefined });
    else if (kind === 'team') {
      const opponents = choice.opponents.map((o, i) =>
        i === Number(index) ? { ...o, team: field.value ? Number(field.value) : undefined } : o,
      );
      update({ opponents });
    } else if (kind === 'faction' || kind === 'character') {
      const opponents = choice.opponents.map((o, i) =>
        i === Number(index)
          ? kind === 'faction'
            ? { ...o, faction: field.value as FactionId }
            : { ...o, character: field.value as PersonalityId }
          : o,
      );
      update({ opponents });
    }
  };
}
